import { useQuery } from "@tanstack/react-query";

import { getPrivateInstance } from "../../_privateInstance";
import { aalReasonsPath, parseAalList } from "../aalV2";

export type WithdrawalsTopReasonsFilters = {
  start_date: string;
  end_date: string;
  agent_id?: string;
  agent_role_id?: string;
};

export type WithdrawalsTopReasonRow = {
  reason: string;
  transactions_count: number;
  distinct_users_count: number;
};

export type WithdrawalsTopReasonsQueryOptions = {
  enabled?: boolean;
};

const buildReasonsParams = (filters: WithdrawalsTopReasonsFilters) => {
  const params: Record<string, string> = {
    start_date: filters.start_date,
    end_date: filters.end_date,
  };
  if (filters.agent_id?.trim()) {
    params.agent_id = filters.agent_id.trim();
  }
  if (filters.agent_role_id?.trim()) {
    params.agent_role_id = filters.agent_role_id.trim();
  }
  return params;
};

async function fetchTopReasons(
  filters: WithdrawalsTopReasonsFilters,
  decision: "approved" | "declined",
): Promise<WithdrawalsTopReasonRow[]> {
  const response = await getPrivateInstance().get(aalReasonsPath(decision), {
    params: buildReasonsParams(filters),
  });
  return parseAalList<WithdrawalsTopReasonRow>(response?.data);
}

export const useGetAALWithdrawalsTopDeclinedReasons = (
  filters: WithdrawalsTopReasonsFilters,
  options?: WithdrawalsTopReasonsQueryOptions,
) => {
  const { start_date, end_date, agent_id, agent_role_id } = filters;
  const extraEnabled = options?.enabled !== false;

  return useQuery<WithdrawalsTopReasonRow[], Error>({
    queryKey: [
      "AGENT_AUDIT_LOG_WITHDRAWALS_TOP_DECLINED",
      start_date,
      end_date,
      agent_id,
      agent_role_id,
    ],
    queryFn: () => fetchTopReasons(filters, "declined"),
    enabled: Boolean(start_date && end_date) && extraEnabled,
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  });
};

export const useGetAALWithdrawalsTopApprovedReasons = (
  filters: WithdrawalsTopReasonsFilters,
  options?: WithdrawalsTopReasonsQueryOptions,
) => {
  const { start_date, end_date, agent_id, agent_role_id } = filters;
  const extraEnabled = options?.enabled !== false;

  return useQuery<WithdrawalsTopReasonRow[], Error>({
    queryKey: [
      "AGENT_AUDIT_LOG_WITHDRAWALS_TOP_APPROVED",
      start_date,
      end_date,
      agent_id,
      agent_role_id,
    ],
    queryFn: () => fetchTopReasons(filters, "approved"),
    enabled: Boolean(start_date && end_date) && extraEnabled,
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  });
};
